import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  BillingDurationResponse,
  BulkCampaignRow,
  CdrSearchRequest,
  CdrSearchResponse,
  CreateReportScheduleRequest,
  ReportDownloadRow,
  ReportParameterRow,
  ReportScheduleLogRow,
  ReportScheduleRow,
  ResellerSubUserOption,
  ResellerSummaryRequest,
  ResellerSummaryRow,
  SubUserReportRequest,
  SubUserReportRow,
  UpdateReportScheduleRequest,
  VoicePlanLogRow,
} from './types';

export const reportsKeys = {
  voicePlanLogs: (userId: string | number | null) =>
    ['reports', 'voice-plan-logs', userId] as const,
  resellerSubUsers: (userId: string | number | null) =>
    ['reports', 'reseller', 'sub-users', userId] as const,
  downloadList: (userId: string | number | null) =>
    ['reports', 'download', userId] as const,
  scheduleList: (userId: string | number | null) =>
    ['reports', 'schedule', userId] as const,
  scheduleLogs: (scheduleId: string | number) =>
    ['reports', 'schedule', 'logs', scheduleId] as const,
  scheduleParams: ['reports', 'schedule', 'params'] as const,
  downloadWebhookList: (userId: string | number | null) =>
    ['reports', 'download', 'webhook', userId] as const,
};

/* --------------------------------------------------------------- *
 * VoicePlanLogs — single GET per logged-in user.                  *
 * --------------------------------------------------------------- */

export function useVoicePlanLogs() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: reportsKeys.voicePlanLogs(userId),
    queryFn: () =>
      api.get<VoicePlanLogRow[]>(`/api/obd/get/user/voice/plan/logs/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<VoicePlanLogRow>(data),
  });
}

/* --------------------------------------------------------------- *
 * CDR — search-on-submit. Use a mutation so we control when it    *
 * runs (mobile is required + dates must be valid).                *
 * --------------------------------------------------------------- */

export function useCdrSearch() {
  return useMutation({
    mutationFn: (req: CdrSearchRequest) =>
      api.post<CdrSearchResponse>('/api/obd/search/cdr', req),
  });
}

/* --------------------------------------------------------------- *
 * Sub-User Report — search-on-submit; also an initial "load all"  *
 * call on mount which we drive from the page itself.              *
 * --------------------------------------------------------------- */

export function useSubUserReport() {
  return useMutation({
    mutationFn: (req: SubUserReportRequest) =>
      api.post<SubUserReportRow[]>('/api/obd/sub/user/report/list', req),
  });
}

/* --------------------------------------------------------------- *
 * Reseller Summary — list of sub-users + on-demand summary fetch. *
 * --------------------------------------------------------------- */

export function useResellerSubUsers() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: reportsKeys.resellerSubUsers(userId),
    queryFn: () =>
      api.get<ResellerSubUserOption[]>(`/api/obd/reseller/users/${userId}`),
    enabled: Boolean(userId),
    staleTime: 5 * 60_000,
    select: (data) =>
      ensureArray<ResellerSubUserOption>(data).sort((a, b) =>
        (a.username ?? '').localeCompare(b.username ?? ''),
      ),
  });
}

export function useResellerSummary() {
  return useMutation({
    mutationFn: (req: ResellerSummaryRequest) =>
      api.post<ResellerSummaryRow[]>('/api/obd/reseller/summary', req),
  });
}

export function useBillingDuration() {
  return useMutation({
    mutationFn: (campaignId: string | number) =>
      api.get<BillingDurationResponse>(
        `/api/obd/campaign/billing/duration/${campaignId}`,
      ),
  });
}

/* --------------------------------------------------------------- *
 * Download Reports                                                 *
 * --------------------------------------------------------------- */

export function useDownloadReports() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: reportsKeys.downloadList(userId),
    queryFn: () =>
      api.get<ReportDownloadRow[]>(`/api/obd/report/download/${userId}`),
    enabled: Boolean(userId),
    staleTime: 10_000,
    refetchInterval: (query) => {
      const rows = query.state.data;
      if (Array.isArray(rows) && rows.some((r) => String(r.status) !== '2')) {
        return 10_000; // Poll every 10 sec while any report is pending
      }
      return false; // Stop polling when all reports are ready
    },
    select: (data) => ensureArray<ReportDownloadRow>(data),
  });
}

export function useDownloadWebhookReports() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: reportsKeys.downloadWebhookList(userId),
    queryFn: () =>
      api.get<ReportDownloadRow[]>(`/api/obd/webhook/report/download/${userId}`),
    enabled: Boolean(userId),
    staleTime: 10_000,
    refetchInterval: (query) => {
      const rows = query.state.data;
      if (Array.isArray(rows) && rows.some((r) => String(r.status) !== '2')) {
        return 10_000;
      }
      return false;
    },
    select: (data) => ensureArray<ReportDownloadRow>(data),
  });
}

export function useBulkReportCampaigns(reportId: number | string | null) {
  return useQuery({
    queryKey: ['reports', 'bulk-campaigns', reportId],
    queryFn: () =>
      api.get<BulkCampaignRow[]>(`/api/obd/report/bulk/campaigns/${reportId}`),
    enabled: Boolean(reportId),
    staleTime: 60_000,
    select: (data) => ensureArray<BulkCampaignRow>(data),
  });
}

/* --------------------------------------------------------------- *
 * Report Scheduling                                                *
 * --------------------------------------------------------------- */

export function useReportSchedules() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: reportsKeys.scheduleList(userId),
    queryFn: () =>
      api.get<ReportScheduleRow[]>(`/api/obd/report/schedule/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<ReportScheduleRow>(data),
  });
}

export function useReportParameters() {
  return useQuery({
    queryKey: reportsKeys.scheduleParams,
    queryFn: () => api.get<ReportParameterRow[]>('/api/obd/report_parameters'),
    staleTime: 5 * 60_000,
    select: (data) => ensureArray<ReportParameterRow>(data),
  });
}

function useInvalidateSchedules() {
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.userId);
  return () => qc.invalidateQueries({ queryKey: reportsKeys.scheduleList(userId) });
}

export function useCreateReportSchedule() {
  const invalidate = useInvalidateSchedules();
  return useMutation({
    mutationFn: (req: CreateReportScheduleRequest) =>
      api.post<{ message?: string }>('/api/obd/report/schedule', req),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateReportSchedule() {
  const invalidate = useInvalidateSchedules();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: UpdateReportScheduleRequest & { id: number }) =>
      api.put<{ message?: string }>(`/api/obd/report/schedule/${id}`, body),
    onSuccess: () => invalidate(),
  });
}

export function useReportScheduleLogs(scheduleId: string | number | null) {
  return useQuery({
    queryKey: reportsKeys.scheduleLogs(scheduleId ?? 'none'),
    queryFn: () =>
      api.get<ReportScheduleLogRow[]>(
        `/api/obd/report/schedule/${scheduleId}/logs`,
      ),
    enabled: Boolean(scheduleId),
    staleTime: 30_000,
    select: (data) => ensureArray<ReportScheduleLogRow>(data),
  });
}
